import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Clock,
  Gift,
  Loader2,
  MapPin,
  Phone,
  Plus,
  Radar,
  Target,
  TrendingUp,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { PageNav } from '../components/PageNav';
import { HelpHint } from '../components/HelpHint';
import { useSession } from '../lib/session';
import type { Lead } from '../lib/types';
import { ScrollX } from '../components/ScrollX';

const kes = (amount: number): string => `KES ${amount.toLocaleString('en-KE')}`;

const onDate = (iso: string | null): string =>
  iso
    ? new Intl.DateTimeFormat('en-KE', {
        day: 'numeric',
        month: 'short',
        timeZone: 'Africa/Nairobi',
      }).format(new Date(iso))
    : '—';

/**
 * Coverage carries a word and a shape, never colour alone — "nearby" and
 * "no coverage" are the pair somebody with a red/green deficiency would
 * otherwise have to guess between.
 */
const COVERAGE_TONE: Record<string, string> = {
  served: 'bg-secondary-50 text-secondary-700',
  nearby: 'bg-accent-50 text-accent-700',
  no_coverage: 'bg-danger-50 text-danger-700',
  unknown: 'bg-muted text-muted-foreground',
};

/* ── The pipeline ────────────────────────────────────────────────────────── */

export function LeadsPage() {
  const [mine, setMine] = useState(false);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);

  const leads = useQuery({
    queryKey: ['leads', mine, status, q],
    queryFn: () => api.leads({ mine, status: status || undefined, q: q || undefined }),
  });

  const reference = useQuery({
    queryKey: ['sales', 'reference'],
    queryFn: api.salesReferenceData,
    staleTime: 300_000,
  });

  const stale = leads.data?.leads.filter((lead) => lead.stale) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-1.5 font-heading text-2xl">
            Leads
            <HelpHint>
              Skipping stages is fine — a walk-in who already knows the price goes straight to
              Quoted. The one rule is that a lead cannot be Won until a contract has gone out,
              because that is what conversion and referral rewards are counted on.
            </HelpHint>
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Every enquiry, and what to do about it next.
          </p>
        </div>
        {/* Steps back to an outline while the form it opened is on screen. */}
        <button
          type="button"
          className={adding ? 'btn-secondary' : 'btn-primary'}
          onClick={() => setAdding((v) => !v)}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Log an enquiry
        </button>
      </div>

      {adding && (
        <NewLeadForm sources={reference.data?.sources ?? []} onDone={() => setAdding(false)} />
      )}

      {/* The most actionable number on the page: nobody has touched these. */}
      {stale.length > 0 && (
        <div className="card border-accent-200 bg-accent-50/30">
          <p className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 shrink-0 text-accent-600" aria-hidden="true" />
            <span>
              <strong>{stale.length}</strong>{' '}
              {stale.length === 1 ? 'lead has' : 'leads have'} gone quiet — a new enquiry left a
              day has usually already phoned somebody else.
            </span>
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <input
          type="search"
          className="input max-w-xs"
          placeholder="Name, phone or address"
          value={q}
          onChange={(event) => setQ(event.target.value)}
        />
        <select
          className="input max-w-48"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="">Every stage</option>
          {(reference.data?.statuses ?? []).map((value) => (
            <option key={value} value={value}>
              {value.replace(/_/g, ' ').toLowerCase()}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={mine}
            onChange={(event) => setMine(event.target.checked)}
          />
          Only mine
        </label>
      </div>

      {leads.isPending && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary-500" aria-label="Loading" />
        </div>
      )}

      {leads.data?.leads.length === 0 && (
        <EmptyState
          icon={Target}
          headline={q || status || mine ? 'No leads match that' : 'No leads yet'}
          body={
            q || status || mine
              ? 'Clear the filters to see the whole pipeline.'
              : 'Every enquiry lives here, with what to do about it next. Log the next person who walks in or calls.'
          }
        />
      )}

      <div className="space-y-2">
        {leads.data?.leads.map((lead) => <LeadRow key={lead.id} lead={lead} />)}
      </div>
    </div>
  );
}

function LeadRow({ lead }: { lead: Lead }) {
  return (
    <Link
      to={`/leads/${lead.id}`}
      className="card flex flex-wrap items-center gap-x-4 gap-y-2 transition-colors hover:bg-muted/40"
    >
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium">{lead.name}</span>
          <span className="text-xs text-muted-foreground">{lead.phoneE164}</span>
          {lead.stale && (
            <span className="badge bg-accent-50 text-accent-700">Needs chasing</span>
          )}
        </p>
        <p className="truncate text-sm text-muted-foreground">
          {lead.nextAction}
          {lead.areaName ? ` · ${lead.areaName}` : ''}
          {lead.monthlyValueKes ? ` · ${kes(lead.monthlyValueKes)} a month` : ''}
        </p>
      </div>
      <span className={`badge ${COVERAGE_TONE[lead.coverage] ?? ''}`}>{lead.coverageLabel}</span>
      <span className="badge bg-muted text-muted-foreground">{lead.statusLabel}</span>
      <span className="w-full text-xs text-muted-foreground sm:w-auto sm:text-right">
        {lead.ownerName ?? 'Nobody assigned'} · {onDate(lead.lastActivityAt ?? lead.createdAt)}
      </span>
    </Link>
  );
}

function NewLeadForm({ sources, onDone }: { sources: string[]; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    source: 'WALK_IN',
    addressLine: '',
    referralCode: '',
  });

  const create = useMutation({
    mutationFn: () =>
      api.createLead({
        name: form.name,
        phone: form.phone,
        email: form.email || undefined,
        source: form.source,
        addressLine: form.addressLine || undefined,
        referralCode: form.referralCode || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
      onDone();
    },
  });

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  return (
    <section className="card">
      <h3 className="mb-3 font-heading text-base">Log an enquiry</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium">Name</span>
          <input className="input mt-1" value={form.name} onChange={set('name')} />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Phone</span>
          <input
            className="input mt-1"
            value={form.phone}
            onChange={set('phone')}
            placeholder="0712 345 678"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Email (optional)</span>
          <input className="input mt-1" value={form.email} onChange={set('email')} />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Where did they come from?</span>
          <select className="input mt-1" value={form.source} onChange={set('source')}>
            {sources.map((source) => (
              <option key={source} value={source}>
                {source.replace(/_/g, ' ').toLowerCase()}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-medium">Address or estate</span>
          <input className="input mt-1" value={form.addressLine} onChange={set('addressLine')} />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Referral code (optional)</span>
          <input
            className="input mt-1 uppercase"
            value={form.referralCode}
            onChange={set('referralCode')}
            placeholder="GH4K0P"
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            If they were sent by an existing customer.
          </span>
        </label>
      </div>

      {create.isError && (
        <p className="mt-3 rounded-xl bg-danger-50 p-3 text-sm text-danger-700">
          {(create.error as ApiError).message}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          className="btn-primary"
          disabled={create.isPending || form.name.length < 2 || form.phone.length < 6}
          onClick={() => create.mutate()}
        >
          {create.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Save
        </button>
        <button type="button" className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
      </div>
    </section>
  );
}

/* ── One lead ────────────────────────────────────────────────────────────── */

export function LeadDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [lostReason, setLostReason] = useState('NO_RESPONSE');

  const lead = useQuery({ queryKey: ['lead', id], queryFn: () => api.lead(id) });
  const reference = useQuery({
    queryKey: ['sales', 'reference'],
    queryFn: api.salesReferenceData,
    staleTime: 300_000,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['lead', id] });
    void queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  const move = useMutation({
    mutationFn: (to: string) =>
      api.moveLead(id, {
        to,
        note: note || undefined,
        lostReason: to === 'LOST' ? lostReason : undefined,
      }),
    onSuccess: () => {
      setNote('');
      refresh();
    },
  });

  const logActivity = useMutation({
    mutationFn: (kind: string) => api.logLeadActivity(id, { kind, body: note || undefined }),
    onSuccess: () => {
      setNote('');
      refresh();
    },
  });

  const coverage = useMutation({ mutationFn: () => api.checkCoverage(id), onSuccess: refresh });

  if (lead.isPending) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }
  if (lead.isError) {
    return <p className="card text-danger-700">{(lead.error as ApiError).message}</p>;
  }

  const data = lead.data;
  const failure = [move.error, logActivity.error, coverage.error].find(Boolean) as
    | ApiError
    | undefined;

  return (
    // Wide enough for its table — see UsersPage for the reasoning.
    <div className="mx-auto max-w-6xl space-y-5">
      <PageNav crumbs={[{ label: 'Leads', to: '/leads' }, { label: data.name }]} backTo="/leads" />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl">{data.name}</h2>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Phone className="h-3.5 w-3.5" aria-hidden="true" />
              {data.phoneE164}
            </span>
            {data.areaName && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                {data.areaName}
              </span>
            )}
          </p>
        </div>
        <span className="badge bg-muted text-muted-foreground">{data.statusLabel}</span>
      </div>

      <div className="card">
        <p className="text-sm">
          <span className="font-medium">Next: </span>
          {data.nextAction}
        </p>
      </div>

      {failure && (
        <p className="rounded-xl bg-danger-50 p-3 text-sm text-danger-700">{failure.message}</p>
      )}

      <section className="card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-heading text-base">
            <Radar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Coverage
          </h3>
          <button
            type="button"
            className="btn-secondary text-sm"
            disabled={coverage.isPending}
            onClick={() => coverage.mutate()}
          >
            {coverage.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Check the network
          </button>
        </div>
        <p className="mt-2">
          <span className={`badge ${COVERAGE_TONE[data.coverage] ?? ''}`}>
            {data.coverageLabel}
          </span>
        </p>
        {coverage.data && <p className="mt-2 text-sm text-muted-foreground">{coverage.data.detail}</p>}
        {data.coverage === 'no_coverage' && (
          <p className="mt-2 text-xs text-muted-foreground">
            Keep this lead rather than deleting it. A list of addresses that wanted service and
            could not get it is a build plan.
          </p>
        )}
      </section>

      <section className="card">
        <h3 className="mb-3 font-heading text-base">Move it along</h3>
        <label className="block">
          <span className="text-sm font-medium">A note (optional)</span>
          <textarea
            className="input mt-1 min-h-16"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Spoke to her, wants it before the end of the month."
          />
        </label>

        <div className="mt-3 flex flex-wrap gap-2">
          {(reference.data?.activityKinds ?? ['CALL', 'WHATSAPP', 'VISIT', 'NOTE']).map((kind) => (
            <button
              key={kind}
              type="button"
              className="btn-secondary text-sm"
              disabled={logActivity.isPending}
              onClick={() => logActivity.mutate(kind)}
            >
              Log {kind.toLowerCase()}
            </button>
          ))}
        </div>

        <div className="mt-4 border-t border-border pt-4">
          <p className="mb-2 text-sm font-medium">Change the stage</p>
          <div className="flex flex-wrap gap-2">
            {(reference.data?.statuses ?? [])
              .filter((value) => value !== data.status && value !== 'LOST')
              .map((value) => (
                <button
                  key={value}
                  type="button"
                  className="btn-secondary text-sm"
                  disabled={move.isPending}
                  onClick={() => move.mutate(value)}
                >
                  {value.replace(/_/g, ' ').toLowerCase()}
                </button>
              ))}
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="block">
              <span className="text-sm font-medium">If it is lost, why?</span>
              <select
                className="input mt-1 max-w-56"
                value={lostReason}
                onChange={(event) => setLostReason(event.target.value)}
              >
                {(reference.data?.lostReasons ?? []).map((value) => (
                  <option key={value} value={value}>
                    {value.replace(/_/g, ' ').toLowerCase()}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn-secondary"
              disabled={move.isPending}
              onClick={() => move.mutate('LOST')}
            >
              Mark lost
            </button>
          </div>
        </div>
      </section>

      {/* Where it went, if it went anywhere. */}
      {(data.contract || data.convertedCustomer || data.installJob) && (
        <section className="card">
          <h3 className="mb-2 font-heading text-base">What came of it</h3>
          <ul className="space-y-1.5 text-sm">
            {data.contract && (
              <li>
                Contract{' '}
                <Link
                  to={`/contracts/${data.contract.id}`}
                  className="text-primary-600 hover:underline"
                >
                  {data.contract.reference}
                </Link>{' '}
                <span className="text-muted-foreground">({data.contract.status.toLowerCase()})</span>
              </li>
            )}
            {data.convertedCustomer && <li>Customer {data.convertedCustomer.name}</li>}
            {data.installJob && (
              <li>
                Installation{' '}
                <Link
                  to={`/tickets/${data.installJob.id}`}
                  className="text-primary-600 hover:underline"
                >
                  {data.installJob.reference}
                </Link>
              </li>
            )}
          </ul>
        </section>
      )}

      <section className="card">
        <h3 className="mb-3 font-heading text-base">History</h3>
        {data.activities.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing logged yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {data.activities.map((activity) => (
              <li key={activity.id} className="py-2.5 text-sm">
                <p className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium">
                    {activity.kind === 'STATUS_CHANGE'
                      ? `${activity.fromStatus} → ${activity.toStatus}`
                      : activity.kind.toLowerCase()}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {activity.by?.name ?? 'system'} · {onDate(activity.occurredAt)}
                  </span>
                </p>
                {activity.body && (
                  <p className="mt-0.5 text-muted-foreground">{activity.body}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/* ── Referrals ───────────────────────────────────────────────────────────── */

export function ReferralsPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const referrals = useQuery({ queryKey: ['referrals'], queryFn: () => api.referrals() });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['referrals'] });
  const approve = useMutation({ mutationFn: api.approveReferral, onSuccess: refresh });
  const markPaid = useMutation({ mutationFn: api.markReferralPaid, onSuccess: refresh });
  const issueAll = useMutation({ mutationFn: api.issueAllReferralCodes, onSuccess: refresh });

  const rows = referrals.data?.referrals ?? [];
  const failure = [approve.error, markPaid.error].find(Boolean) as ApiError | undefined;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-1.5 font-heading text-2xl">
            Referrals
            <HelpHint>
              The system works out when a referral qualifies — the person they sent has been
              connected for thirty days. It never approves one. Paying is a decision a person
              makes, and only an admin can make it.
            </HelpHint>
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Word of mouth, with a ledger behind it.
          </p>
        </div>
        {can('referral:approve') && (
          <button
            type="button"
            className="btn-secondary"
            disabled={issueAll.isPending}
            onClick={() => issueAll.mutate()}
          >
            {issueAll.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Give every customer a code
          </button>
        )}
      </div>

      {issueAll.data && (
        <p className="card text-sm">
          Issued {issueAll.data.issued} new code{issueAll.data.issued === 1 ? '' : 's'}.
        </p>
      )}
      {failure && (
        <p className="rounded-xl bg-danger-50 p-3 text-sm text-danger-700">{failure.message}</p>
      )}

      {referrals.isPending && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary-500" aria-label="Loading" />
        </div>
      )}

      {rows.length === 0 && !referrals.isPending && (
        <EmptyState
          icon={Gift}
          headline="No referrals yet"
          body="Every customer has a code on their own page. Rewards appear here once somebody uses one and stays connected for a month."
        />
      )}

      <div className="space-y-2">
        {rows.map((referral) => (
          <div key={referral.id} className="card">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium">{referral.referrerName}</span>
                  <span className="text-xs text-muted-foreground">
                    referred {referral.referredName}
                  </span>
                </p>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Code {referral.code} · {referral.rewardLabel}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {referral.qualificationDetail}
                </p>
              </div>
              <span className="badge bg-muted text-muted-foreground">
                {referral.status.toLowerCase()}
              </span>
            </div>

            {can('referral:approve') && (
              <div className="mt-3 flex flex-wrap gap-2">
                {/*
                  Outlined, not orange — deliberately. This is a per-row action
                  in a list, so an orange one would appear five or fifty times
                  on a screen that is allowed exactly one. A list of equal rows
                  has no single primary action, and inventing one by colouring
                  the first row would be a lie.
                */}
                {referral.readyToApprove && (
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    disabled={approve.isPending}
                    onClick={() => approve.mutate(referral.id)}
                  >
                    <Gift className="h-4 w-4" aria-hidden="true" />
                    Approve the reward
                  </button>
                )}
                {referral.status === 'APPROVED' && (
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    disabled={markPaid.isPending}
                    onClick={() => markPaid.mutate(referral.id)}
                  >
                    Mark it given
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── The funnel ──────────────────────────────────────────────────────────── */

export function SalesReportPage() {
  const report = useQuery({ queryKey: ['sales', 'report'], queryFn: () => api.salesReport() });

  if (report.isPending) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }
  if (report.isError) {
    return <p className="card text-danger-700">{(report.error as ApiError).message}</p>;
  }

  const data = report.data;
  const widest = Math.max(...data.funnel.map((row) => row.count), 1);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h2 className="font-heading text-2xl">Sales</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Your funnel, and which of the things we do actually brings customers.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="In the pipeline" value={String(data.headline.open)} />
        <Stat label="Won" value={String(data.headline.won)} />
        <Stat
          label="Conversion"
          value={
            data.headline.conversionRate === null
              ? '—'
              : `${Math.round(data.headline.conversionRate * 100)}%`
          }
          hint="Of the leads that reached a decision"
        />
        <Stat
          label="Won, per month"
          value={kes(data.headline.wonMonthlyValueKes)}
          hint="Recurring revenue the won leads represent"
        />
      </div>

      {data.headline.stale > 0 && (
        <div className="card border-accent-200 bg-accent-50/30 text-sm">
          <strong>{data.headline.stale}</strong> leads have gone quiet. That is the most
          actionable number on this page.
        </div>
      )}

      <section className="card">
        <h3 className="mb-3 flex items-center gap-2 font-heading text-base">
          <TrendingUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          The funnel
        </h3>
        <ul className="space-y-2">
          {data.funnel.map((row) => (
            <li key={row.status} className="flex items-center gap-3">
              <span className="w-32 shrink-0 text-sm">{row.label}</span>
              <div className="h-6 flex-1 overflow-hidden rounded-lg bg-muted">
                <div
                  className="h-full rounded-lg bg-primary-500"
                  style={{ width: `${Math.round((row.count / widest) * 100)}%` }}
                />
              </div>
              <span className="w-10 shrink-0 text-right text-sm tabular-nums">{row.count}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">
          Every stage is shown, including the empty ones — a funnel with the zeroes removed
          hides the one fact worth knowing.
        </p>
      </section>

      <section className="card">
        <h3 className="mb-3 font-heading text-base">Where customers come from</h3>
        <ScrollX>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="py-2">Source</th>
                <th className="py-2 text-right">Leads</th>
                <th className="py-2 text-right">Won</th>
                <th className="py-2 text-right">Rate</th>
                <th className="py-2 text-right">Monthly value</th>
              </tr>
            </thead>
            <tbody>
              {data.bySource.map((row) => (
                <tr key={row.source} className="border-b border-border last:border-0">
                  <td className="py-2">{row.label}</td>
                  <td className="py-2 text-right tabular-nums">{row.total}</td>
                  <td className="py-2 text-right tabular-nums">{row.won}</td>
                  <td className="py-2 text-right tabular-nums">
                    {row.rate === null ? '—' : `${Math.round(row.rate * 100)}%`}
                  </td>
                  <td className="py-2 text-right tabular-nums">{kes(row.monthlyValueKes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      </section>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="card">
          <h3 className="mb-2 font-heading text-base">Why we lose</h3>
          {data.lostReasons.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing lost yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {data.lostReasons.map((row) => (
                <li key={row.key} className="flex justify-between">
                  <span>{row.key.replace(/_/g, ' ').toLowerCase()}</span>
                  <span className="tabular-nums">{row.count}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            "No coverage" is not a sales failure — it is a build plan with a number on it.
          </p>
        </section>

        <section className="card">
          <h3 className="mb-2 flex items-center gap-2 font-heading text-base">
            <Gift className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Referrals
          </h3>
          <ul className="space-y-1 text-sm">
            <li className="flex justify-between">
              <span>Customers won through a referral</span>
              <span className="tabular-nums">{data.referrals.wonThroughReferral}</span>
            </li>
            <li className="flex justify-between">
              <span>Ready to approve</span>
              <span className="tabular-nums">{data.referrals.readyToApprove}</span>
            </li>
            <li className="flex justify-between">
              <span>Approved or paid</span>
              <span className="tabular-nums">
                {data.referrals.approved + data.referrals.paid}
              </span>
            </li>
            <li className="flex justify-between">
              <span>Cash committed</span>
              <span className="tabular-nums">{kes(data.referrals.committedCashKes)}</span>
            </li>
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">
            Cash and bill credits only. Free days cost something too, but not something this
            can honestly put a figure on.
          </p>
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="card">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
